import React from 'react';
import cx from 'classnames';
import { string, arrayOf, shape, func, oneOf, bool } from 'prop-types';
import locationShape from 'propTypes/locationShape';
import SortIcon from 'icons/sort.inline.svg';
import formatUrl from 'helpers/formatUrl';

import styles from './Sorting.scss';

const SORT_ASC = 'asc';
const SORT_DSC = 'dsc';

/**
 * This is generic sorting component. It takes a list of options from which it
 * knows what buttons to create and how they interact with the query
 * part of the url.
 *
 * The object uses the "function as child" pattern to pass its output to the
 * child component. The active sorting type and order are passed in this way. The
 * child function can then decide how to interpret these values and sort its
 * data accordingly. This makes that the component only needs to care about
 * layout and url interaction and not know about sorting logic which would likely
 * be different for each use case.
 */
function Sorting({
  className,
  text,
  location,
  queryValueKey,
  queryOrderKey,
  options,
  children,
  historyReplace,
}) {
  // Take the sorting value from the url query and fallback to whatever option
  // has been marked as default.
  const currentSortValue =
    location.query[queryValueKey] || options.find(({ isDefault }) => isDefault === true).queryValue;

  // Take the sorting order from the url query and fallback to whatever initial
  // value has been set for the currently active option.
  const currentSortOrder =
    location.query[queryOrderKey] ||
    options.find(({ queryValue }) => queryValue === currentSortValue).initialOrder;

  const { pathname, query } = location;

  return (
    <div className={cx(styles.main, className)}>
      <div className={styles.header}>
        <span className={styles.label}>
          {text}
        </span>
        <ul className={styles.buttonList}>
          {options.map(({ label, queryValue, initialOrder }) => {
            const isActive = queryValue === currentSortValue;

            const nextOrder = isActive
              ? currentSortOrder === SORT_ASC ? SORT_DSC : SORT_ASC
              : initialOrder;

            const nextLocation = {
              pathname,
              query: {
                ...query,
                [queryValueKey]: queryValue,
                [queryOrderKey]: nextOrder,
              },
            };
            const nextUrl = formatUrl(nextLocation);

            return (
              <li key={label} className={styles.buttonListItem}>
                <a
                  className={cx(styles.button, {
                    [styles.isActive]: isActive,
                    [styles.asc]: isActive && currentSortOrder === SORT_ASC,
                    [styles.dsc]: isActive && currentSortOrder === SORT_DSC,
                  })}
                  href={nextUrl}
                  onClick={evt => {
                    evt.preventDefault();
                    historyReplace(nextLocation);
                  }}
                >
                  {label}
                  <span
                    className={cx(styles.sortOrderIndicator, {
                      [styles.flip]: isActive && currentSortOrder === SORT_DSC,
                    })}
                  >
                    <SortIcon className={styles.sortIcon} />
                    <SortIcon className={styles.sortIcon} />
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
      <div className={styles.content}>
        {children({ sortValue: currentSortValue, sortOrder: currentSortOrder })}
      </div>
    </div>
  );
}

Sorting.propTypes = {
  className: string,
  location: locationShape.isRequired,
  queryValueKey: string.isRequired,
  queryOrderKey: string.isRequired,
  text: string,
  options: arrayOf(
    shape({
      label: string.isRequired,
      queryValue: string.isRequired,
      initialSorting: oneOf([SORT_ASC, SORT_DSC]),
      isDefault: bool,
    })
  ),
  children: func.isRequired,
  historyReplace: func.isRequired,
};

export default Sorting;



// WEBPACK FOOTER //
// ./src/components/DataPanel/Overview/Sorting/index.js